import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getToken } from '../api/client';
import { monitor } from '../api/monitor';
import { useAuth } from './AuthContext';
import { usePrefs } from './PrefsContext';
import { useToast } from './ToastContext';
import { ALERT_TYPES } from '../utils/metrics';

const LiveContext = createContext(null);
export const useLive = () => useContext(LiveContext);
const MAX_FEED = 400;

/**
 * One shared Server-Sent-Events connection for the whole app.
 * - `latest`  newest reading per station number
 * - `feed`    rolling list of the newest readings (newest first)
 * - `alertTick` increments whenever an alert is raised or resolved, so pages can refetch
 */
export function LiveProvider({ children }) {
  const { user } = useAuth();
  const { toasts } = usePrefs();
  const toast = useToast();
  const [status, setStatus] = useState('connecting');
  const [latest, setLatest] = useState({});
  const [feed, setFeed] = useState([]);
  const [alertTick, setAlertTick] = useState(0);
  const [lastAt, setLastAt] = useState(null);
  const [rate, setRate] = useState(0);
  const stamps = useRef([]);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;
  const seq = useRef(0);

  // Pre-fill the feed with the most recent stored readings so pages are not empty while waiting for the next one
  useEffect(() => {
    if (!user) return;
    monitor.readings({ limit: 80 }).then((res) => {
      const rows = [...res.data].reverse().map((r) => ({ ...r, _id: ++seq.current, _receivedAt: new Date(r.publishedAt).getTime() }));
      const key = (r) => `${r.deviceName}|${r.publishedAt}`;
      setFeed((f) => {
        const seen = new Set(f.map(key));
        return [...f, ...rows.filter((r) => !seen.has(key(r)))].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)).slice(0, MAX_FEED);
      });
      setLatest((m) => {
        const out = { ...m };
        rows.forEach((r) => { if (!out[r.station] || new Date(out[r.station].publishedAt) < new Date(r.publishedAt)) out[r.station] = r; });
        return out;
      });
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let es;
    let retry;
    let closed = false;
    const connect = () => {
      setStatus('connecting');
      es = new EventSource(`/api/v1/monitor/live?token=${encodeURIComponent(getToken())}`);
      es.addEventListener('hello', () => setStatus('live'));
      es.addEventListener('reading', (ev) => {
        const r = JSON.parse(ev.data);
        r._id = ++seq.current;
        r._receivedAt = Date.now();
        setStatus('live');
        setLastAt(r._receivedAt);
        setLatest((m) => ({ ...m, [r.station]: r }));
        setFeed((f) => [r, ...f].slice(0, MAX_FEED));
        const now = Date.now();
        stamps.current = [...stamps.current.filter((t) => now - t < 60000), now];
        setRate(stamps.current.length);
      });
      es.addEventListener('alert', (ev) => {
        const { kind, alert } = JSON.parse(ev.data);
        setAlertTick((n) => n + 1);
        if (!toastsRef.current) return;
        const label = ALERT_TYPES[alert.type]?.label || alert.type;
        if (kind === 'new') toast.push({ tone: alert.severity === 'critical' ? 'crit' : 'warn', title: `${label} · ${alert.stationName || `Station ${alert.station}`}`, text: alert.message });
        else toast.push({ tone: 'ok', title: `Resolved: ${label}`, text: alert.stationName || `Station ${alert.station}` });
      });
      es.onerror = () => {
        setStatus('offline');
        es.close();
        if (!closed) retry = setTimeout(connect, 4000);
      };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); es && es.close(); };
  }, [user, toast]);

  // the per-minute counter decays when nothing arrives
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      stamps.current = stamps.current.filter((x) => now - x < 60000);
      setRate(stamps.current.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const value = useMemo(() => ({ status, latest, feed, alertTick, lastAt, rate }), [status, latest, feed, alertTick, lastAt, rate]);
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}
